import PropTypes from 'prop-types';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  RollingNumberWrapper,
  RollingNumberColumn,
  RollingNumberItem,
} from './styles.js';

export const RollingNumber = ({ value, start = 10, isInView = false }) => {
  const wrapperRef = useRef(null);
  const itemRef = useRef(null);
  const [itemHeight, setItemHeight] = useState(0);

  const effectiveStart = Math.min(start, value);
  const targetIndex = value - effectiveStart;
  const numbers = useMemo(
    () =>
      Array.from(
        { length: targetIndex + 1 },
        (_, index) => effectiveStart + index,
      ),
    [effectiveStart, targetIndex],
  );

  useLayoutEffect(() => {
    const measureHeight = () => {
      const measuredHeight =
        itemRef.current?.offsetHeight ?? wrapperRef.current?.offsetHeight ?? 0;
      setItemHeight(measuredHeight);
    };

    measureHeight();

    const observer = new ResizeObserver(measureHeight);
    if (itemRef.current) observer.observe(itemRef.current);
    if (wrapperRef.current) observer.observe(wrapperRef.current);

    return () => observer.disconnect();
  }, [numbers.length]);

  const shouldAnimate = isInView && itemHeight > 0;
  const translateY = shouldAnimate ? -targetIndex * itemHeight : 0;
  const duration = targetIndex === 0 ? 0 : Math.min(1 + targetIndex * 0.02, 3);

  return (
    <RollingNumberWrapper ref={wrapperRef}>
      <RollingNumberColumn
        data-testid="rolling-number-column"
        $translateY={translateY}
        $duration={duration}
        $isReady={shouldAnimate}
      >
        {numbers.map((number, index) => (
          <RollingNumberItem key={number} ref={index === 0 ? itemRef : null}>
            {number}
          </RollingNumberItem>
        ))}
      </RollingNumberColumn>
    </RollingNumberWrapper>
  );
};

RollingNumber.displayName = 'RollingNumber';

RollingNumber.propTypes = {
  value: PropTypes.number.isRequired,
  start: PropTypes.number,
  isInView: PropTypes.bool,
};
